import React, { lazy, Suspense } from "react";

import { FaCheck } from "react-icons/fa6";
import { Helmet } from "react-helmet-async";
import HeadImageApp1 from "@/assets/citywise/waggex-payroll-software-gurgaon-hero-section.webp";
import HeadImageAppMobile from "@/assets/citywise/waggex-payroll-software-gurgaon-hero-section.webp";

const Whywaggexcommonchoice = lazy(
  () => import("./common/Whywaggexcommonchoice"),
);
const LookingforPayroll = lazy(() => import("./common/LookingforPayroll"));
const HomeFaq = lazy(() => import("../../pages/HomeFaq"));
const AppDownloadCard = lazy(() => import("../common/AppDownloadCard"));
const PayrollBanner = lazy(() => import("../common/PayrollBanner"));
const ModernizeHR = lazy(() => import("../common/ModernizeHR"));

const Gurgaon = () => {
  const faq1 = [
    {
      title: "Why do businesses in Gurgaon need payroll software?",
      content:
        "Payroll software helps businesses automate salary calculations, attendance, leave management, statutory deductions, and payslip generation. It reduces manual work, improves payroll accuracy, and helps HR teams manage employees more efficiently.",
    },
    {
      title: "Can payroll and attendance be managed from one platform?",
      content:
        "Yes. Payroll and attendance are fully connected, allowing businesses to calculate salaries using attendance records, approved leave, overtime, and working hours without maintaining separate spreadsheets.",
    },
    {
      title:
        "Can employees manage attendance, leave, and payslips through a mobile app?",
      content:
        "Yes. Employees can mark attendance, submit leave requests, download payslips, view attendance history, and receive company notifications directly from the Waggex Staff App, reducing routine HR requests.",
    },
    {
      title:
        "Does the software support AI face recognition and GPS attendance?",
      content:
        "Yes. Waggex supports AI-powered face recognition, GPS attendance, geo-fencing, and selfie-based check-ins, helping businesses improve attendance accuracy and prevent proxy attendance.",
    },
  ];

  const faq2 = [
    {
      title:
        "Is the software suitable for startups and growing businesses in Gurgaon?",
      content:
        "Yes. Waggex is designed for startups, SMEs, IT companies, consultancies, retail businesses, manufacturing units, and enterprises looking to simplify payroll, attendance, and workforce management.",
    },
    {
      title: "Does Waggex include leave and shift management?",
      content:
        "Yes. Managers can create shifts, assign schedules, approve leave requests, monitor leave balances, and manage employee availability through a centralized dashboard.",
    },
    {
      title: "Can employees download payslips online?",
      content:
        "Yes. Employees can securely access and download their monthly payslips through the Waggex Staff App or employee self-service portal anytime.",
    },
    {
      title: "Does Waggex provide payroll reports and compliance records?",
      content:
        "Yes. Waggex generates payroll reports, salary registers, attendance summaries, statutory records, and payroll-ready reports that help businesses maintain accurate HR and compliance documentation.",
    },
  ];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",

      name: "Waggex Payroll Software Gurgaon",

      url: "https://www.waggex.com/best-payroll-software-in-gurgaon",

      image: "https://www.waggex.com/waggex-payroll-software-logo.png",

      description:
        "Best payroll software in Gurgaon for businesses and SMEs. Automate payroll processing, attendance tracking, leave management, employee records, and workforce management with Waggex.",

      applicationCategory: "BusinessApplication",

      operatingSystem: "Web, Android, iOS",

      featureList: [
        "Payroll software Gurgaon",
        "Attendance management software Gurgaon",
        "Leave and shift management Gurgaon",
        "Face recognition attendance Gurgaon",
        "Employee self-service app",
        "GPS attendance tracking",
        "Automated payroll processing",
        "AI selfie attendance",
      ],

      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
        description: "Free Trial Available",
      },

      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        ratingCount: "250",
        bestRating: "5",
        worstRating: "1",
      },

      provider: {
        "@type": "Organization",
        name: "Waggex",
        url: "https://www.waggex.com/",
        logo: "https://www.waggex.com/waggex-payroll-software-logo.png",

        areaServed: {
          "@type": "Country",
          name: "India",
        },

        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+91-01724534553",
          email: "info@waggex.com",
          contactType: "customer support",
          availableLanguage: ["English", "Hindi"],
        },
      },
    },

    {
      "@context": "https://schema.org",
      "@type": "FAQPage",

      mainEntity: [
        {
          "@type": "Question",
          name: "Which is the best payroll software in Gurgaon?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The best payroll software depends on your business requirements. Waggex helps Gurgaon businesses automate payroll, attendance management, leave tracking, and payslip generation from one connected platform.",
          },
        },
        {
          "@type": "Question",
          name: "Does Waggex support attendance-based payroll?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex links employee attendance, approved leave, and working hours directly to payroll, so salaries are calculated accurately without manual cross-checking.",
          },
        },
        {
          "@type": "Question",
          name: "Does Waggex provide mobile attendance?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Employees can mark attendance through the Waggex Staff App using selfie attendance, GPS attendance, and mobile check-ins, making it ideal for office staff, remote employees, field teams, and service professionals.",
          },
        },
        {
          "@type": "Question",
          name: "Does Waggex support face recognition attendance?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex offers AI-powered FaceLens face recognition attendance for secure, contactless employee verification and accurate attendance tracking without additional hardware.",
          },
        },
        {
          "@type": "Question",
          name: "Is Waggex suitable for startups and SMEs in Gurgaon?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex is designed for startups, SMEs, IT companies, retail businesses, consultancies, manufacturing units, and growing enterprises looking for an affordable payroll and HRMS solution.",
          },
        },
      ],
    },

    {
      "@context": "https://schema.org",
      "@type": "Organization",

      name: "Waggex",

      url: "https://www.waggex.com/",

      logo: "https://www.waggex.com/waggex-payroll-software-logo.png",

      description:
        "Waggex provides payroll software, HRMS, attendance management, leave management, and workforce management solutions for businesses in Gurgaon and across India.",

      areaServed: "IN",

      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+91-01724534553",
        email: "info@waggex.com",
        contactType: "customer support",
        availableLanguage: ["English", "Hindi"],
      },

      sameAs: ["https://www.linkedin.com/company/waggex"],
    },

    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",

      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.waggex.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Payroll Software Gurgaon",
          item: "https://www.waggex.com/best-payroll-software-in-gurgaon",
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Best Payroll Software in Gurgaon for Businesses | Waggex</title>
        <meta
          name="description"
          content="Looking for payroll software in Gurgaon? Waggex simplifies payroll, attendance, leave management, compliance, and HR operations for businesses. Book a free demo today"
        />
        <meta
          name="keywords"
          content="payroll software in Gurgaon,payroll software Gurgaon,HR software for businesses in Gurgaon,attendance management software Gurgaon,HRMS software Gurgaon,online payroll system Gurgaon,employee management software Gurgaon,leave management software Gurgaon,face recognition attendance Gurgaon,payroll software for IT companies Gurgaon,payroll software for service companies Gurgaon"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/best-payroll-software-in-gurgaon"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="relative overflow-hidden py-10 px-6 bg-[#D1DFEA]">
        {/* Desktop Background Image */}

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <h1 className="text-xl sm:text-3xl font-roboto font-bold text-[#10324F] leading-tight max-w-2xl mx-auto">
            Best Payroll Software in Gurgaon for
            <br /> Startups & Companies
          </h1>

          <p className="mt-5 text-sm sm:text-base lg:text-lg text-[#10324F] leading-relaxed max-w-4xl mx-auto">
            Payroll processing becomes more challenging as businesses grow.
            <strong> Waggex </strong> helps Gurgaon businesses automate payroll
            calculations, attendance tracking, leave management, and employee
            records from one centralized platform, reducing manual work,
            improving payroll accuracy, and simplifying day-to-day HR operations
          </p>

          {/* Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() =>
                (window.location.href = "https://app.waggex.com/signup")
              }
              className="bg-[#17324D] text-white px-8 py-3 rounded-xl font-medium shadow-md hover:bg-[#10263B] transition-all w-full sm:w-auto"
            >
              Try Free for 5 Employees
            </button>

            <button
              onClick={() => (window.location.href = "/contact-us")}
              className="border border-[#17324D] text-[#17324D] px-8 py-3 rounded-xl font-medium hover:bg-[#17324D] hover:text-white transition-all w-full sm:w-auto"
            >
              Book Free Demo
            </button>
          </div>

          {/* Mobile Image */}
          <div className="hidden lg:block">
            <div className="-mt-30 flex justify-center">
              <img
                src={HeadImageAppMobile}
                width={1112}
                height={194}
                fetchPriority="high"
                alt="Waggex AI attendance management software Gurgaon GPS tracking, geo-fencing, selfie attendance and face recognition attendance"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Designed for Gurgaon's Modern Workforce
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base leading-relaxed">
              From corporate offices and IT companies to startups and consulting
              firms, businesses in Gurgaon need payroll software that keeps pace
              with growing teams. A centralized platform simplifies payroll
              processing, attendance, leave management, and employee records
              while reducing HR effort and improving operational efficiency
            </p>
          </div>
          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Automated Payroll Processing</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Shift Management</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Attendance-Based Salary Calculation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>PDF Payslip Generation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>AI Selfie Attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee Self-Service App</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>GPS Attendance Tracking</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Real-Time Notifications</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee Leave Management</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Face Recognition Attendance</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[#F3F5F7] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Payroll Built for Growing Gurgaon Businesses
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              As businesses in Gurgaon expand, payroll becomes more complex with
              larger teams, multiple shifts, attendance records, and salary
              structures. A connected payroll platform simplifies every payroll
              cycle, helping HR teams save time, reduce manual work, and improve
              payroll accuracy
            </p>
          </div>
          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Automated payroll calculation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee salary reports</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Attendance-based payroll processing</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Payroll history and records</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Overtime and shift salary management</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee payroll notifications</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Payslip generation in PDF format</span>
              </div>
            </div>
          </div>{" "}
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center max-w-3xl mx-auto leading-loose">
            Employees receive instant salary updates and can access payslips
            whenever they need them, while HR teams spend less time responding
            to payroll-related requests
          </p>
        </div>
      </section>
      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Smarter Attendance Management with AI
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Whether your workforce operates from the office, on the field, or
              across multiple locations, accurate attendance is essential for
              payroll and productivity. AI-powered attendance, GPS tracking, and
              face recognition help businesses simplify attendance management
              while reducing manual HR work
            </p>
          </div>
          {/* List */}
          <div className="mt-8 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>AI Selfie Attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Shift-Based Attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>GPS Attendance Tracking</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Mobile Attendance Marking</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Geofencing</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Real-Time Attendance Reports</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>FaceLens AI Face Recognition Attendance</span>
              </div>
            </div>
          </div>{" "}
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center">
            Automatically sync attendance with payroll and employee records to
            save time, improve accuracy, and streamline workforce management
            every day
          </p>
        </div>
      </section>
      <section className="bg-[#F3F5F7] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Smarter Leave & Shift Management
            </h2>

            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Simplify leave requests and shift scheduling with automated
              approval workflows and real-time notifications. Reduce manual
              coordination, improve workforce planning, and keep employees and
              managers aligned throughout the approval process
            </p>
          </div>

          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Yearly leave allocation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Leave approval workflow</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Leave balance tracking</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Real-time leave notifications</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Attendance-based leave management</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Shift management</span>
              </div>
            </div>{" "}
          </div>
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center max-w-2xl mx-auto leading-loose">
            Employees receive instant updates when leave requests are submitted,
            approved, or rejected, while managers can review and respond to
            requests quickly from one centralized platform
          </p>
        </div>
      </section>
      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Everything Employees Need in One App
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Give employees instant access to attendance, leave requests,
              payslips, and important workplace information through the
              <strong> Waggex Staff App</strong>
            </p>
            <p className="mt-4 text-[#10324F] text-lg">Employee Can:</p>
          </div>
          {/* List */}
          <div className="mt-8 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Mark attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>View attendance history</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Apply for leave </span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Receive notifications</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Download payslips</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Access employee records</span>
              </div>
            </div>
          </div>{" "}
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center">
            <strong> Waggex Staff App</strong> improves employee accessibility
            while reducing HR manual work across all routine attendance, leave,
            and payroll-related requests throughout the month
          </p>
        </div>
      </section>
      <section className="bg-[#F3F5F7] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Trusted by Businesses Across Industries
            </h2>

            <p className="mt-4 text-[#10324F] text-lg">
              Waggex is suitable for:
            </p>
          </div>

          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Startups</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Factories</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>SMEs</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Service Companies</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>IT Companies</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Field Workforce Businesses</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Retail Businesses</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Agencies & Consultancies</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <Whywaggexcommonchoice
          heading="The Smarter Way to Manage Payroll & HR"
          description="Growing businesses need payroll software that does more than process salaries. By bringing payroll, attendance, leave management, and employee records together, businesses can reduce manual work, improve operational efficiency, and manage their workforce with greater accuracy and control"
          paragraph=""
        />
        <LookingforPayroll
          heading="Looking For Payroll Software In Chennai?"
          subHeading="Book a Free Demo and Explore Waggex Features"
          description="Manage payroll, attendance, leaves, shifts, and employee operations using one smart HR and payroll platform"
          features={[
            "AI Attendance Management",
            "Payroll Automation",
            "Employee Self-Service App",
            "GPS & Face Recognition Attendance",
            "Attendance-Based Payroll",
            "Real-Time Notifications",
          ]}
          buttonText="Book Free Demo"
        />
        <HomeFaq
          faq1={faq1}
          faq2={faq2}
          subtitle="Everything you need to know about Waggex payroll software in India"
        />
        {/*  <AppDownloadCard /> */}
        <PayrollBanner
          heading="See How Easy Payroll Can Be"
          description="Experience payroll software designed to save time, reduce manual work, and improve accuracy—with no obligation to continue"
          buttonText="View HRMS Software"
          buttonLink="https://waggex.com/"
        />
        <AppDownloadCard />
        <ModernizeHR />
      </Suspense>
    </>
  );
};

export default Gurgaon;
